import React, { FC, useEffect, useRef, useState, useMemo } from "react";
import Heading from "components/Heading/Heading";
import Card4 from "components/Card4/Card4";
import Card7 from "components/Card7/Card7";
import Card9 from "components/Card9/Card9";
import Card10 from "components/Card10/Card10";
import Card11 from "components/Card11/Card11";
import Card10V2 from "components/Card10/Card10V2";
import NextPrev from "components/NextPrev/NextPrev";
import Glide from "@glidejs/glide";
import { PostDataType } from "data/types";
import ncNanoId from "utils/ncNanoId";
import { useInView } from "react-intersection-observer";
import ButtonPrimary from "components/Button/ButtonPrimary";
export interface SectionSliderPostsProps {
  className?: string;
  heading: string;
  subHeading?: string;
  posts: PostDataType[];
  postCardName?: "card4" | "card7" | "card9" | "card10" | "card10V2" | "card11";
  sliderStype?: "style1" | "style2";
  perView?: 2 | 3 | 4;
  uniqueSliderClass: string;
  onShowAll?: () => void; 
  totalCount?: number; 
}

const SectionSliderPosts: FC<SectionSliderPostsProps> = ({
  heading,
  subHeading,
  className = "",
  posts,
  postCardName = "card4",
  sliderStype = "style2",
  perView = 4,
  uniqueSliderClass,
  onShowAll,
  totalCount,
}) => {
  const [showAll, setShowAll] = useState(false);
  const UNIQUE_CLASS = useMemo(
    () => `SectionSliderPosts_${ncNanoId(uniqueSliderClass)}`,
    [uniqueSliderClass]
  );
  const glideRef = useRef<HTMLDivElement>(null);

  const { ref: containerRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  // Glide mount logic
  useEffect(() => {
    if (!inView || !glideRef.current || showAll || posts.length === 0) return;

    let glideInstance: Glide | null = null;
    let rafId: number | null = null;

    rafId = requestAnimationFrame(() => {
      if (!glideRef.current) return;
      glideInstance = new Glide(glideRef.current, {
        perView: perView,
        gap: 32,
        bound: true,
        breakpoints: {
          1280: {
            perView: perView - 1,
          },
          1023: {
            perView: perView - 2 || 1.2,
            gap: 20,
          },
          767: {
            perView: perView - 2 || 1.2,
            gap: 20,
          },
          639: {
            perView: 1.2,
            gap: 20,
          },
        },
      });

      glideInstance.mount();
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (glideInstance) {
        try {
          glideInstance.destroy();
        } catch (_) {}
      }
    };
  }, [inView, perView, showAll, posts.length]);

  const getPostComponent = () => {
    switch (postCardName) {
      case "card7": return Card7;
      case "card9": return Card9;
      case "card10": return Card10;
      case "card10V2": return Card10V2;
      case "card11": return Card11;
      case "card4":
      default: return Card4;
    }
  };
 
  const handleShowMoreClick = () => {
    setShowAll(true);
    onShowAll?.();
  };

  const renderHeading = () => {
    return (
      <Heading desc={subHeading} hasNextPrev={sliderStype === "style1"} isCenter={sliderStype === "style2"}>
        {heading}
      </Heading>
    );
  };

  const CardName = getPostComponent();

  return (
    <div className={`nc-SectionSliderPosts ${className}`} ref={containerRef}>
      {inView && (
        <div className={`${UNIQUE_CLASS}`} ref={glideRef}>
          {renderHeading()}
          <div
            className={showAll ? "grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 mt-8 lg:mt-10" : "glide__track mt-8 lg:mt-10"}
            data-glide-el={!showAll ? "track" : undefined}
          >
            <ul className={showAll ?  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8" : "glide__slides"}>
              {posts.map((item, index) => (
                <li
                  key={index}
                  className={showAll ? "list-none" : `glide__slide h-auto ${
                  sliderStype === "style2" ? "pb-12 xl:pb-16" : ""
                  }`}
                >
                  <CardName post={item} />
                </li>
              ))}
            </ul>
          </div>
          {!showAll && posts.length > 0 && (
            <div className="mt-10 flex flex-col items-center justify-center space-y-5 sm:space-y-0 sm:flex-row sm:justify-center relative">          {sliderStype === "style2" && (
            <NextPrev
              btnClassName="w-12 h-12"
              containerClassName="justify-center"
            />
          )}
          
           {(totalCount ? totalCount > posts.length : posts.length >= 20) && (
            <div className="sm:absolute sm:right-0">
              <ButtonPrimary onClick={handleShowMoreClick}>
                Show me more
              </ButtonPrimary>
            </div>
          )}
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionSliderPosts;
